import { track } from '@eventbrite/datalayer-library';
import { Button } from '@eventbrite/eds-button';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import classNames from 'classnames';
import { Field, Form, Formik, useFormikContext } from 'formik';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect } from 'react';
import * as Yup from 'yup';
import { HEAP_NEW_ACCOUNT_VERIFY_PASSWORD_MODAL_SHOWN_EVENT } from '../../../constants/analytics';
import {
    FORM_FIELD_INVALID_EMAIL,
    FORM_FIELD_NO_ACCOUNT,
    FORM_FIELD_REQUIRED,
} from '../../../constants/loginMessages';
import {
    IConfigContextProps,
    useConfigContext,
} from '../../../contexts/ConfigContext';
import {
    IFrontendContext,
    useFrontendContext,
} from '../../../contexts/FrontendContext';
import {
    INotificationContext,
    useNotificationsContext,
} from '../../../contexts/NotificationsContext';
import { IUserContext, useUserContext } from '../../../contexts/UserContext';
import EmailField from '../../common/EmailField';
import FormSubmitButton from '../../common/FormSubmitButton';

interface ILookupFormComponentProps {
    onSubmit: (values: any) => void;
    buttonCTA: GenericLazyString | string;
    shouldAnimate: boolean;

    trackOnPageLoad?: () => void;
}

const LookUpSchema = Yup.object().shape({
    email: Yup.string()
        .email(FORM_FIELD_INVALID_EMAIL.toString())
        .required(FORM_FIELD_REQUIRED.toString()),
});

const FormObserver = ({ isSubmitting }: { isSubmitting: boolean }) => {
    const formikContext = useFormikContext();
    const userContext: IUserContext = useUserContext();

    useEffect(() => {
        if (formikContext?.setFieldError && isSubmitting) {
            formikContext.setFieldError('email', '');
        }
    }, [isSubmitting]); // eslint-disable-line

    useEffect(() => {
        if (formikContext?.setFieldError) {
            if (userContext?.state?.lookup?.lookupResponse?.exists === false) {
                track({
                    eventName:
                        HEAP_NEW_ACCOUNT_VERIFY_PASSWORD_MODAL_SHOWN_EVENT,
                    eventData: {},
                });
                formikContext.setFieldError(
                    'email',
                    FORM_FIELD_NO_ACCOUNT.toString(),
                );
            } else {
                formikContext.setFieldError('email', '');
            }
        }
    }, [userContext?.state?.lookup?.lookupResponse]); // eslint-disable-line

    return null;
};

const LookupForm = ({
    onSubmit,
    buttonCTA,
    shouldAnimate,

    trackOnPageLoad,
}: ILookupFormComponentProps) => {
    const frontendContext: IFrontendContext = useFrontendContext();
    const userContext: IUserContext = useUserContext();
    const notificationsContext: INotificationContext =
        useNotificationsContext();
    const configContext: IConfigContextProps = useConfigContext();
    const socialData = userContext?.state?.socialData;
    const setGeneralNotification =
        notificationsContext?.methods?.setGeneralNotification;
    const isReferrerMyTickets = decodeURIComponent(
        configContext?.referrer ?? '',
    ).includes('/mytickets');

    useEffect(() => {
        if (trackOnPageLoad && !isEmpty(socialData)) {
            trackOnPageLoad();
        }
        return () => {
            setGeneralNotification({});
        };
    }, [setGeneralNotification, socialData, trackOnPageLoad]);

    const emailFieldClasses = 'eds-l-mar-bot-6';

    const buttonClasses = classNames({
        'eds-fx--fade-in-up eds-fx--delay-1': shouldAnimate,
    });

    const initialValues: { email: string } = { email: '' };

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={LookUpSchema}
            onSubmit={async (values: { email: string }) => {
                onSubmit?.({
                    values,
                    contexts: {
                        frontendContext,
                        userContext,
                        notificationsContext,
                        configContext,
                    },
                });
            }}
        >
            {({ values, setFieldValue, isSubmitting }: any) => (
                <Form data-testid="form-lookup">
                    {isReferrerMyTickets && (
                        <FormObserver isSubmitting={isSubmitting} />
                    )}
                    <Field
                        name="email"
                        component={EmailField}
                        value={values.email}
                        // submitFailed={submitFailed}
                        buttonClasses={emailFieldClasses}
                        isStatic={true}
                        setFieldValue={setFieldValue}
                        suggestDomains={true}
                    />

                    <div className="eds-l-pad-bot-10">
                        <FormSubmitButton
                            buttonClasses={buttonClasses}
                            submitDisabled={
                                frontendContext?.state.lookupOngoing
                            }
                            isLoading={frontendContext?.state.lookupOngoing}
                            buttonCTA={buttonCTA}
                        />

                        {configContext.showBackButton && (
                            <div className="eds-l-mar-top-3">
                                <Button
                                    type="button"
                                    style="link"
                                    size="block"
                                    onClick={() => {
                                        history.back();
                                    }}
                                >
                                    {gettext('Back')}
                                </Button>
                            </div>
                        )}
                    </div>
                </Form>
            )}
        </Formik>
    );
};

export default LookupForm;
